import {Injectable} from '@angular/core';
import {catchError, forkJoin, map, Observable, of} from "rxjs";
import {TrackingResponse} from "../entities";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {environment} from "../../environments/environment";
import {AuthService} from "./auth.service";

const CONTAINER_NUMBER_REGEX = /^[a-zA-Z]{3}[uUjJzZ]{1}[0-9]{7}$/;


@Injectable({
  providedIn: 'root'
})
export class TrackingResultService {

  private readonly baseUrl = environment.apiUrl;


  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders().set("x-api-key", this.authService.getApiKey());
  }


  searchByBookingNumber(bookingNumber: string): Observable<TrackingResponse> {
    const url = this.baseUrl + "/subscription/equipment";
    const params = {bookingNumber};
    return this.http.get<any>(url, {params, headers: this.getHeaders()}).pipe(
      map(r => {
        const first = r.results?.[0];
        return {
          results: r.results,
          type: "CARRIER_BOOKING_NUMBER",
          searchString: bookingNumber,
          from: this.formatLocation(first?.POL),
          to: this.formatLocation(first?.POD),
        };
      })
    );
  }


  searchByContainerNumber(equipmentNumber: string): Observable<TrackingResponse> {
    const url = this.baseUrl + "/subscription/equipment";
    const params = {equipmentNumber};
    return this.http.get<any>(url, {params, headers: this.getHeaders()}).pipe(
      map(r => {
        const first = r.results?.[0];
        return {
          results: r.results,
          type: "CONTAINER_NUMBER",
          searchString: equipmentNumber,
          from: this.formatLocation(first?.POL),
          to: this.formatLocation(first?.POD),
        };
      })
    );
  }


  searchByBillNumber(billNumber: string): Observable<TrackingResponse> {
    const url = this.baseUrl + "/subscription/equipment";
    const params = {billNumber};
    return this.http.get<any>(url, {params, headers: this.getHeaders()}).pipe(
      map(r => {
        const first = r.results?.[0];
        return {
          results: r.results,
          type: "BILL_OF_LADING" as const,
          searchString: billNumber,
          from: this.formatLocation(first?.POL),
          to: this.formatLocation(first?.POD),
        };
      })
    );
  }


  private formatLocation(loc: any): string {
    if (!loc?.address) return '';
    const {city, country} = loc.address;
    return [city, country].filter(Boolean).join(', ');
  }

  search(searchString: string): Observable<TrackingResponse> {
    searchString = searchString.trim();
    if (searchString.match(CONTAINER_NUMBER_REGEX)) return this.searchByContainerNumber(searchString);

    return forkJoin({
      booking: this.searchByBookingNumber(searchString).pipe(map(r => r), catchError(() => of(null))),
      bill: this.searchByBillNumber(searchString).pipe(map(r => r), catchError(() => of(null))),
    }).pipe(
      map(({booking, bill}) => {
        const bookingHasResults = (booking?.results?.length ?? 0) > 0;
        const billHasResults = (bill?.results?.length ?? 0) > 0;
        if (bookingHasResults) return booking!;
        if (billHasResults) return bill!;
        return booking!;
      })
    );
  }
}
